import { useWindowSize } from 'react-use';
import { useElementsUIContext } from '../../context';
import { motion, useDragControls } from 'framer-motion';
import { IconButton } from '../Button';
import { floatingMessagingThreadWindowVariants } from './animation/variants';
import { ConversationMessagesSkeletonSection } from '../ConversationMessagesSkeleton';
import clsx from 'clsx';
import { UseMeasureRef } from 'react-use/lib/useMeasure';
import { FloatingOverlay } from '@floating-ui/react';
import { useState } from 'react';

export interface FloatingMessagingThreadWindowProps {
  close: () => void;
  isLoading: boolean;
  body: React.ReactNode;
  disableScroll: boolean;
  footer: React.ReactNode;
  footerRef: UseMeasureRef<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  dragConstraintsAdjustments?: { top: number; left: number; right: number; bottom: number };
}

export function FloatingMessagingThreadWindow({
  close,
  body,
  footer,
  footerRef,
  isLoading,
  disableScroll,
  scrollContainerRef,
  dragConstraintsAdjustments = { bottom: 0, left: 0, right: 0, top: 0 },
}: FloatingMessagingThreadWindowProps) {
  const dragControls = useDragControls();
  const { sideOverOpen } = useElementsUIContext();
  const [lockBodyScroll, setLockBodyScroll] = useState(false);
  const { height: windowHeight, width: windowWidth } = useWindowSize();

  // Some of these seem like "magic numbers" so here is an explanation of what they are:
  // - 24 a nice padding from the edge of the screen
  // - 680 is the width of the dashboard sidebar (constant as of 11/12/2024)
  const floatingWindowHeight = 692 as const;
  const floatingWindowWidth = 440 as const;
  const dragConstraints = sideOverOpen
    ? {
        top: 24 + dragConstraintsAdjustments?.top,
        left: 24 + dragConstraintsAdjustments?.left,
        right: windowWidth - floatingWindowWidth - 680 + dragConstraintsAdjustments?.right,
        bottom: windowHeight - floatingWindowHeight - 24 + dragConstraintsAdjustments?.bottom,
      }
    : {
        top: 24 + dragConstraintsAdjustments?.top,
        left: 24 + dragConstraintsAdjustments?.left,
        right: windowWidth - floatingWindowWidth + dragConstraintsAdjustments?.right,
        bottom: windowHeight - floatingWindowHeight - 24 + dragConstraintsAdjustments?.bottom,
      };

  return (
    <>
      {lockBodyScroll && <FloatingOverlay lockScroll />}
      <motion.div
        drag
        exit="exit"
        initial="initial"
        dragListener={false}
        className="z-50 fixed"
        dragControls={dragControls}
        dragConstraints={dragConstraints}
        onPointerOut={() => setLockBodyScroll(false)}
        onPointerOver={() => setLockBodyScroll(true)}
        onPointerEnter={() => setLockBodyScroll(true)}
        onPointerLeave={() => setLockBodyScroll(false)}
        onPointerCancel={() => setLockBodyScroll(false)}
        variants={floatingMessagingThreadWindowVariants}
        transition={{ ease: 'easeInOut', duration: 0.5 }}
        animate={sideOverOpen ? 'sideOverOpen' : 'sideOverClosed'}
        style={{ height: floatingWindowHeight, width: floatingWindowWidth }}
        dragTransition={{ power: 0.1, timeConstant: 250, modifyTarget: (target) => Math.round(target) }}
        custom={{ x: dragConstraints.right, openY: dragConstraints.bottom, initialY: dragConstraints.bottom + 60 }}
      >
        <div className="relative h-full w-full shadow-classic bg-darkMode-gray-dark rounded-[20px]">
          <motion.div
            layoutScroll
            ref={scrollContainerRef}
            className={clsx(
              disableScroll ? 'overflow-hidden' : 'overflow-auto',
              'grid grid-rows-[74px_1fr_auto] rounded-[20px] h-full'
            )}
          >
            <motion.div
              whileDrag={{ cursor: 'grabbing' }}
              onPointerDown={(e) => {
                dragControls.start(e);
                // Prevent default and stop propagation to avoid the drag event triggering background element selection
                e.preventDefault();
                e.stopPropagation();
              }}
              className="h-[74px] z-10 border-b-black border-b backdrop-blur-[8px] sticky flex items-center p-5 top-0 border-opacity-40 w-full cursor-pointer rounded-t-2xl bg-darkMode-gray-dark/50"
            >
              <IconButton onClick={close} icon="close" theme="text" />
            </motion.div>
            <div className="pb-6 flex items-end">
              {isLoading ? (
                <div className="grid gap-12 px-6 pt-6 w-full h-full content-end">
                  <ConversationMessagesSkeletonSection
                    widths={[
                      { w1: 90, w2: 220 },
                      { w1: 130, w2: 198 },
                    ]}
                  />
                  <ConversationMessagesSkeletonSection
                    widths={[
                      { w1: 109, w2: 170 },
                      { w1: 117, w2: 207 },
                    ]}
                  />
                </div>
              ) : (
                body
              )}
            </div>
            <div
              ref={footerRef}
              className="min-h-[104px] pl-3 pt-4 pb-6 pr-6 h-max bg-androidCard sticky bottom-0 border-t border-darkMode-almostBlack rounded-b-[20px]"
            >
              {footer}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
}
